import { useState, useEffect } from 'react'
import { type Models } from 'node-appwrite'
import { Button } from '@/components/ui/button'
import {
  Check,
  Cpu,
  Zap,
  Terminal,
  MessageSquare,
  Sparkles,
  Shield,
  Server,
  X,
} from 'lucide-react'
import { TelegramModal } from '../landing/TelegramModal'
import { toast } from 'sonner'
import {
  createDeploymentFn,
  streamDeploymentLogsFn,
  verifyDeploymentFn,
} from '@/server/functions/deployments'
import {
  DeploymentStatus,
  type DeploymentStatusType,
} from '@/lib/deployment-status'
import { signInWithGoogleFn } from '@/server/functions/auth'
import { useNavigate } from '@tanstack/react-router'
import { useServerFn } from '@tanstack/react-start'
import { TLlmModel } from '@/server/lib/appwrite.types'
import { getCheckoutUrl } from '@/server/functions/payments'
import { getEnvServer } from '@/lib/utils'

const models = [
  {
    id: 'anthropic/claude-opus-4-5',
    name: 'Claude Opus 4.5',
    icon: 'https://upload.wikimedia.org/wikipedia/commons/b/b0/Claude_AI_symbol.svg',
  },
  {
    id: 'openai/gpt-5.2',
    name: 'GPT-5.2',
    icon: 'https://img.icons8.com/androidL/512/FFFFFF/chatgpt.png',
  },
  // {
  //   id: 'gemini/gemini-3',
  //   name: 'Gemini 3 Flash',
  //   icon: 'https://upload.wikimedia.org/wikipedia/commons/thumb/1/1d/Google_Gemini_icon_2025.svg/960px-Google_Gemini_icon_2025.svg.png',
  // },
]

const channels = [
  {
    id: 'telegram',
    name: 'Telegram',
    icon: 'https://upload.wikimedia.org/wikipedia/commons/thumb/8/82/Telegram_logo.svg/960px-Telegram_logo.svg.png',
    available: true,
  },
  {
    id: 'discord',
    name: 'Discord',
    icon: 'https://scbwi-storage-prod.s3.amazonaws.com/images/discord-mark-blue_rA6tXJo.png',
    available: false,
  },
  {
    id: 'whatsapp',
    name: 'WhatsApp',
    icon: 'https://upload.wikimedia.org/wikipedia/commons/thumb/6/6b/WhatsApp.svg/960px-WhatsApp.svg.png',
    available: false,
  },
]

interface ConfigurationCardProps {
  currentUser: Models.User<{
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [key: string]: any;
  }> | null
  className?: string
  initialValues?: {
    model?: TLlmModel
    channel?: string
    token?: string
    resume?: string
  }
  autoResume?: boolean
}

export function ConfigurationCard({
  currentUser,
  className,
  initialValues,
  autoResume,
}: ConfigurationCardProps) {
  const [selectedModel, setSelectedModel] = useState<TLlmModel>(
    initialValues?.model || 'anthropic/claude-opus-4-5',
  )
  const [selectedChannel, setSelectedChannel] = useState(
    initialValues?.channel || '',
  )
  const [isTelegramModalOpen, setIsTelegramModalOpen] = useState(false)
  const [telegramToken, setTelegramToken] = useState(initialValues?.token || '')
  const [isDeploying, setIsDeploying] = useState(false)
  const [deploymentId, setDeploymentId] = useState<string | null>(null)

  // Load active deployment from localStorage on mount
  useEffect(() => {
    if (!autoResume) return

    const savedId = localStorage.getItem('active_deployment_id')
    if (savedId) {
      setDeploymentId(savedId)
      setIsDeploying(true)

      // Fetch initial deployment details to restore state
      void getLogsAndStatus({ data: { id: savedId } }).then((deployment) => {
        if (deployment) {
          if (deployment.llmModel) setSelectedModel(deployment.llmModel as TLlmModel)
          if (deployment.telegramToken) {
            setSelectedChannel('telegram')
            setTelegramToken(deployment.telegramToken)
          }
          if (deployment.status) {
            setCurrentStatus(deployment.status as DeploymentStatusType)
          }
        }
      })
    }
  }, [])

  // Sync deploymentId to localStorage
  useEffect(() => {
    if (deploymentId) {
      localStorage.setItem('active_deployment_id', deploymentId)
    } else {
      localStorage.removeItem('active_deployment_id')
    }
  }, [deploymentId])

  const [currentStatus, setCurrentStatus] = useState<DeploymentStatusType>(
    DeploymentStatus.INITIATING,
  )
  const [previousStatus, setPreviousStatus] = useState<DeploymentStatusType>(
    DeploymentStatus.INITIATING,
  )
  const [stepTimer, setStepTimer] = useState(0)
  const [isWorkerRunning, setIsWorkerRunning] = useState(true)
  const [pairingCode, setPairingCode] = useState('')
  const [isVerifying, setIsVerifying] = useState(false)
  const navigate = useNavigate()
  const signIn = useServerFn(signInWithGoogleFn)

  const getLogsAndStatus = useServerFn(streamDeploymentLogsFn)

  // Polling Status Updates (2s interval)
  useEffect(() => {
    if (!deploymentId || !isDeploying) return

    let isPollerActive = true
    const pollInterval = setInterval(async () => {
      if (!isPollerActive) return

      try {
        const deployment = await getLogsAndStatus({
          data: { id: deploymentId },
        })
        if (!deployment) {
          localStorage.removeItem('active_deployment_id')
          setIsDeploying(false)
          return
        }
        const newStatus = deployment.status as DeploymentStatusType
        const isComplete =
          newStatus === DeploymentStatus.RUNNING ||
          newStatus === DeploymentStatus.FAILED

        if (newStatus && newStatus !== currentStatus) {
          if (newStatus !== DeploymentStatus.FAILED) {
            setPreviousStatus(newStatus)
          }
          setCurrentStatus(newStatus)
          setStepTimer(0)
        }

        if (isComplete) {
          isPollerActive = false
          clearInterval(pollInterval)
          setIsWorkerRunning(false)
          localStorage.removeItem('active_deployment_id')

          if (newStatus === DeploymentStatus.RUNNING) {
            toast.success(
              'Deployment complete! Your instance is now running in the background.',
            )
            setTimeout(() => {
              void navigate({ to: '/dashboard' })
            }, 5000)
          } else if (newStatus === DeploymentStatus.FAILED) {
            toast.error(
              'Deployment failed. Please check the logs or try again.',
            )
          }
        }
      } catch (error) {
        console.error('Polling error:', error)
      }
    }, 2000)

    return () => {
      isPollerActive = false
      clearInterval(pollInterval)
    }
  }, [deploymentId, isDeploying, currentStatus, navigate, getLogsAndStatus])

  // Step Timer Effect
  useEffect(() => {
    if (
      !isDeploying ||
      currentStatus === DeploymentStatus.RUNNING ||
      currentStatus === DeploymentStatus.FAILED
    )
      return

    const interval = setInterval(() => {
      setStepTimer((t) => t + 1)
    }, 1000)

    return () => clearInterval(interval)
  }, [isDeploying])

  const handleChannelClick = (channelId: string) => {
    console.log({ channelId })

    if (channelId === 'telegram') {
      // Only convert to telegram if already have token, otherwise open modal first
      if (telegramToken) {
        setSelectedChannel('telegram')
      } else {
        setIsTelegramModalOpen(true)
      }
    }
  }

  const handleTelegramConnect = (token: string) => {
    setTelegramToken(token)
    setSelectedChannel('telegram')
    toast.success('Telegram bot connected successfully!')
  }

  const handleDeploy = async () => {
    if (!currentUser) {
      toast.error('Please sign in to deploy a Claw instance.')
      return
    }

    // VALIDATION: Require Channel
    if (!selectedChannel) {
      toast.error('Please select a communication channel.')
      return
    }

    // VALIDATION: Check for Token (redundant if selection logic is strict, but safe)
    if (selectedChannel === 'telegram' && !telegramToken) {
      toast.error('Please connect your Telegram bot first.')
      setIsTelegramModalOpen(true)
      return
    }
    if (!currentUser.prefs.plan) {
      const startProductId = await getEnvServer({ data: 'STARTER_PRODUCT_ID' })
      const return_url = new URL(window.location.origin)
      return_url.searchParams.set('model', selectedModel)
      return_url.searchParams.set('channel', selectedChannel)
      return_url.searchParams.set('token', telegramToken || '')
      return_url.pathname = '/new'
      const res = await getCheckoutUrl({ data: { productId: startProductId, userId: currentUser.$id, return_url: return_url.toString() } })
      window.open(res.href)
      return
    }

    setIsDeploying(true)
    try {
      const name = `Claw-${selectedModel.replace(/[^a-zA-Z0-9]/g, '-')}-${Math.floor(Math.random() * 1000)}`
      const { deployment } = await createDeploymentFn({
        data: {
          name,
          llmModel: selectedModel,
          telegramToken: telegramToken || undefined,
        },
      })

      setDeploymentId(deployment.$id)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      console.error('Deployment failed:', error)
      toast.error(error?.message || 'Failed to provision instance. Please try again.')
      setIsDeploying(false)
    }
  }

  const verify = useServerFn(verifyDeploymentFn)
  const handleVerify = async () => {
    if (!pairingCode) return
    setIsVerifying(true)
    try {
      await verify({ data: { id: deploymentId!, code: pairingCode } })
      toast.success('Pairing successful! Starting engine...')
    } catch (error: unknown) {
      console.error('Verification failed:', error)
      const message = error instanceof Error ? error.message : 'Failed to verify pairing code'
      toast.error(message)
    } finally {
      setIsVerifying(false)
    }
  }

  const handleSignIn = async () => {
    const return_url = new URL(window.location.origin)
    return_url.searchParams.set('model', selectedModel)
    return_url.searchParams.set('channel', selectedChannel)
    return_url.searchParams.set('token', telegramToken || '')
    return_url.pathname = '/new'

    try {
      await signIn({ data: { redirect: return_url.toString() } })
    } catch (error) {
      console.error('Sign-in failed:', error)
      toast.error('Failed to sign in. Please try again.')
    }
  }

  return (
    <>
      <div
        className={`relative rounded-3xl border border-orange-300/20 bg-gradient-to-b from-[#131a2e] to-[#0c1220] backdrop-blur-3xl p-[1px] shadow-2xl shadow-black/50 overflow-hidden h-fit flex flex-col justify-center ${className}`}
      >
        {/* Gradient Border Effect */}
        <div className="absolute inset-0 rounded-3xl bg-gradient-to-b from-white/15 via-white/5 to-white/[0.02] pointer-events-none" />
        <div className="absolute inset-[1px] rounded-3xl bg-gradient-to-b from-[#131a2e] to-[#0c1220] pointer-events-none" />

        {/* Card Glow Effect */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-32 bg-gradient-to-b from-orange-500/10 to-transparent pointer-events-none" />

        {/* Content Container */}
        <div className="relative p-8 space-y-8">
          {/* Card Header */}
          <div className="relative flex items-center gap-4 pb-6 border-b border-white/[0.06]">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center shadow-lg shadow-orange-500/20">
              <Sparkles className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white tracking-tight">
                Deploy Your AI Agent
              </h2>
              <p className="text-sm text-slate-400 mt-0.5">
                Configure in seconds, deploy instantly
              </p>
            </div>
          </div>
          {isDeploying ? (
            <div className="space-y-8 py-6 animate-in fade-in slide-in-from-bottom-4 duration-700">
              {/* Status Header */}
              <div className="flex items-center justify-between mb-2">
                <div className="space-y-1">
                  <div className="flex items-center gap-3">
                    <h2 className="text-xl font-black text-white tracking-tight">
                      Deployment active
                    </h2>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 px-2 text-[10px] uppercase tracking-wider text-slate-500 hover:text-white hover:bg-white/5"
                      onClick={() => {
                        localStorage.removeItem('active_deployment_id')
                        setIsDeploying(false)
                        navigate({ to: '/dashboard' }).catch(err => console.error(err))
                      }}
                    >
                      Exit View
                    </Button>
                  </div>
                  <p className="text-[10px] font-bold text-[#5A6480] uppercase tracking-widest leading-none">
                    Target: GCP_US_CENTRAL1
                  </p>
                  {isWorkerRunning && (
                    <p className="text-[9px] font-black text-[#22C55E] uppercase tracking-widest leading-none mt-1">
                      ✓ Background Worker Active
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 border border-primary/20">
                  <span className="relative flex h-1.5 w-1.5">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-primary"></span>
                  </span>
                  <span className="text-[10px] font-black text-primary uppercase tracking-[0.2em]">
                    Live Orchestration
                  </span>
                </div>
              </div>

              {/* Simple Progress Steps */}
              <div className="space-y-4">
                {[
                  {
                    id: 'preparing',
                    label: 'Preparing environment',
                    icon: Server,
                    statuses: [DeploymentStatus.INITIATING, DeploymentStatus.PROVISIONING],
                  },
                  {
                    id: 'engine',
                    label: 'Installing AI logic',
                    icon: Cpu,
                    statuses: [DeploymentStatus.SSH, DeploymentStatus.INSTALLING],
                  },
                  {
                    id: 'channel',
                    label: 'Connecting channel',
                    icon: MessageSquare,
                    statuses: [DeploymentStatus.CONNECTING, DeploymentStatus.VERIFYING],
                    channelIcon:
                      selectedChannel === 'telegram'
                        ? 'https://upload.wikimedia.org/wikipedia/commons/thumb/8/82/Telegram_logo.svg/960px-Telegram_logo.svg.png'
                        : selectedChannel === 'discord'
                          ? 'https://scbwi-storage-prod.s3.amazonaws.com/images/discord-mark-blue_rA6tXJo.png'
                          : null,
                  },
                  {
                    id: 'active',
                    label: 'Instance Live',
                    icon: Zap,
                    statuses: [DeploymentStatus.RUNNING],
                  },
                ].map((phase, idx, allPhases) => {
                  const isFailed = currentStatus === DeploymentStatus.FAILED
                  const targetStatusForIndex = isFailed ? previousStatus : currentStatus

                  // Find the index of the phase that contains the current/last-good status
                  // @ts-expect-error asas
                  const activePhaseIdx = allPhases.findIndex(p => (p.statuses).includes(targetStatusForIndex))

                  const phaseIdx = idx
                  // @ts-expect-error asas
                  const isActive = (phase.statuses).includes(currentStatus) || (isFailed && phaseIdx === activePhaseIdx)
                  const isCompleted = !isFailed && phaseIdx < activePhaseIdx || (currentStatus === DeploymentStatus.RUNNING && phaseIdx <= activePhaseIdx)
                  const hasFailedHere = isFailed && phaseIdx === activePhaseIdx

                  return (
                    <div
                      key={phase.id}
                      className={`flex items-center gap-4 transition-all duration-300 ${!isActive && !isCompleted ? 'opacity-40' : 'opacity-100'}`}
                    >
                      <div
                        className={`w-8 h-8 rounded-full flex items-center justify-center transition-all duration-300 ${isCompleted
                          ? 'bg-primary'
                          : hasFailedHere
                            ? 'bg-red-500/20 border-2 border-red-500'
                            : isActive
                              ? 'bg-primary/20 border-2 border-primary'
                              : 'bg-white/5'
                          }`}
                      >
                        {isCompleted ? (
                          <Check className="w-4 h-4 text-black" />
                        ) : hasFailedHere ? (
                          <X className="w-4 h-4 text-red-500" />
                        ) : phase.channelIcon && isActive ? (
                          <img
                            src={phase.channelIcon}
                            className="w-4 h-4"
                            alt={phase.label}
                          />
                        ) : (
                          <phase.icon
                            className={`w-4 h-4 ${isActive ? 'text-primary' : 'text-white/40'}`}
                          />
                        )}
                      </div>

                      <div className="flex-1">
                        <p
                          className={`text-sm font-medium ${hasFailedHere ? 'text-red-500' : isActive ? 'text-white' : 'text-white/60'}`}
                        >
                          {phase.label}
                        </p>
                      </div>

                      {isActive && !isFailed && (
                        <span className="text-xs text-primary font-mono">
                          {stepTimer}s
                        </span>
                      )}
                    </div>
                  )
                })}
              </div>

              {/* Pairing Input View */}
              {currentStatus === DeploymentStatus.VERIFYING && (
                <div className="space-y-4 pt-4 border-t border-white/[0.06] animate-in fade-in slide-in-from-top-4 duration-500">
                  <div className="space-y-2">
                    <label className="text-xs font-semibold text-slate-400 uppercase tracking-widest pl-1">
                      Pairing Code from Bot
                    </label>
                    <div className="relative group">
                      <div className="absolute inset-y-0 left-4 flex items-center pointer-events-none">
                        <Terminal className="w-4 h-4 text-slate-500 group-focus-within:text-orange-400 transition-colors" />
                      </div>
                      <input
                        type="text"
                        value={pairingCode}
                        onChange={(e) => setPairingCode(e.target.value)}
                        placeholder="Enter the code from your telegram bot"
                        className="w-full bg-slate-900/50 border border-white/[0.08] focus:border-orange-500/50 focus:ring-1 focus:ring-orange-500/50 rounded-2xl py-4 pl-12 pr-4 text-white text-sm transition-all outline-none"
                      />
                    </div>
                    <p className="text-[10px] text-slate-500 leading-relaxed px-1">
                      Send{' '}
                      <code className="bg-slate-800 px-1 rounded">/start</code>{' '}
                      to your Telegram bot. It will reply with a pairing code.
                    </p>
                  </div>

                  <Button
                    onClick={handleVerify}
                    disabled={isVerifying || !pairingCode}
                    className="w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-semibold h-12 rounded-xl shadow-lg shadow-orange-500/10 transition-all active:scale-[0.98]"
                  >
                    {isVerifying ? (
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                        Verifying...
                      </div>
                    ) : (
                      'Verify Pairing'
                    )}
                  </Button>
                </div>
              )}
            </div>
          ) : (
            <>
              {/* Model Selection */}
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 rounded-lg bg-orange-500/10 flex items-center justify-center">
                    <Cpu className="w-3.5 h-3.5 text-orange-400" />
                  </div>
                  <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-widest">
                    Select AI Model
                  </h3>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  {models.map((model) => (
                    <button
                      key={model.id}
                      onClick={() => setSelectedModel(model.id as TLlmModel)}
                      className={`group relative flex items-center gap-3 px-4 py-4 rounded-2xl border-2 transition-all duration-300 overflow-hidden ${selectedModel === model.id
                        ? 'bg-gradient-to-br from-orange-500/10 to-orange-600/5 border-orange-500/40 shadow-[0_0_30px_rgba(234,88,12,0.15)]'
                        : 'bg-slate-800/30 border-white/[0.06] hover:border-white/10 hover:bg-slate-800/50'
                        }`}
                    >
                      {/* Selected Glow */}
                      {selectedModel === model.id && (
                        <div className="absolute inset-0 bg-gradient-to-r from-orange-500/5 to-transparent pointer-events-none" />
                      )}
                      <img
                        src={model.icon}
                        className="size-5 relative z-10"
                        alt={model.name}
                      />
                      <span
                        className={`text-sm font-medium relative z-10 ${selectedModel === model.id ? 'text-white' : 'text-slate-400 group-hover:text-slate-300'}`}
                      >
                        {model.name}
                      </span>
                      {selectedModel === model.id && (
                        <div className="ml-auto w-5 h-5 rounded-full bg-orange-500 flex items-center justify-center">
                          <Check className="w-3 h-3 text-white" />
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {/* Channel Selection */}
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 rounded-lg bg-orange-500/10 flex items-center justify-center">
                    <MessageSquare className="w-3.5 h-3.5 text-orange-400" />
                  </div>
                  <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-widest">
                    Communication Channel
                  </h3>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  {channels.map((channel) => (
                    <button
                      key={channel.id}
                      onClick={() => {
                        console.log({ channel })

                        if (channel.available) {
                          handleChannelClick(channel.id)
                        }
                      }}
                      className={`group relative flex flex-col items-center gap-3 px-3 py-5 rounded-2xl border-2 transition-all duration-300 overflow-hidden ${!channel.available
                        ? 'bg-slate-800/10 border-white/[0.04] text-white/20 grayscale opacity-50 cursor-not-allowed'
                        : selectedChannel === channel.id
                          ? 'bg-gradient-to-br from-orange-500/10 to-orange-600/5 border-orange-500/40 shadow-[0_0_30px_rgba(234,88,12,0.15)]'
                          : 'bg-slate-800/30 border-white/[0.06] hover:border-white/10 hover:bg-slate-800/50'
                        }`}
                    >
                      {/* Selected Glow */}
                      {selectedChannel === channel.id && (
                        <div className="absolute inset-0 bg-gradient-to-b from-orange-500/5 to-transparent pointer-events-none" />
                      )}
                      <img
                        src={channel.icon}
                        className={`h-8 relative z-10 ${!channel.available && 'grayscale'}`}
                        alt={channel.name}
                      />
                      <span
                        className={`text-xs font-medium relative z-10 ${selectedChannel === channel.id ? 'text-white' : !channel.available ? 'text-white/30' : 'text-slate-400 group-hover:text-slate-300'}`}
                      >
                        {channel.name}
                      </span>
                      {!channel.available && (
                        <span className="text-[9px] font-bold uppercase text-slate-500 tracking-wider">
                          Soon
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {/* Deploy / Sign In Action */}
              <div className="pt-2 space-y-5">
                {/* Trust Badges */}
                <div className="flex items-center justify-center gap-6 text-xs text-slate-500">
                  <div className="flex items-center gap-1.5">
                    <div className="w-4 h-4 rounded-full bg-green-500/10 flex items-center justify-center">
                      <Shield className="w-2.5 h-2.5 text-green-500" />
                    </div>
                    <span>Isolated VM</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-4 h-4 rounded-full bg-green-500/10 flex items-center justify-center">
                      <Server className="w-2.5 h-2.5 text-green-500" />
                    </div>
                    <span>Instant Deploy</span>
                  </div>
                </div>

                {currentUser ? (
                  <Button
                    size="lg"
                    onClick={handleDeploy}
                    disabled={isDeploying}
                    className="w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-semibold h-14 rounded-2xl shadow-xl shadow-orange-500/25 transition-all duration-300 hover:shadow-orange-500/40 hover:scale-[1.02] active:scale-[0.98]"
                  >
                    <Zap className="w-5 h-5 mr-2" />
                    Deploy AI Agent
                  </Button>
                ) : (
                  <div className="space-y-3">
                    <Button
                      size="lg"
                      className="w-full bg-slate-900/50 border border-white/[0.08] text-white hover:bg-slate-800 hover:border-orange-500/20 hover:text-white font-semibold h-14 rounded-2xl shadow-xl transition-all duration-300 hover:scale-[1.02] active:scale-[0.98]"
                      onClick={handleSignIn}
                    >
                      <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                        <path
                          fill="currentColor"
                          d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                        />
                        <path
                          fill="currentColor"
                          d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                        />
                        <path
                          fill="currentColor"
                          d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                        />
                        <path
                          fill="currentColor"
                          d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                        />
                      </svg>
                      Continue with Google
                    </Button>
                    <p className="text-xs text-slate-500 text-center">
                      Free to start • No credit card required
                    </p>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
      <TelegramModal
        isOpen={isTelegramModalOpen}
        onClose={() => setIsTelegramModalOpen(false)}
        onConnect={handleTelegramConnect}
      />
    </>
  )
}
